import { useState, useEffect, useRef } from 'react';
import { Send, Github, CheckCircle } from 'lucide-react';
import { socialLinks } from '@/data/siteData';
import { scrollReveal, prefersReducedMotion } from '@/hooks/useGsap';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';

const iconMap: Record<string, React.ReactNode> = {
  github: <Github className="h-5 w-5" />,
};

const Contact = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (sectionRef.current) {
      scrollReveal(sectionRef.current);
    }
  }, []);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsLoading(true);

    // Prepare message for Telegram
    const text = `Hi, I'm ${formData.name} (${formData.email}).\n\n${formData.message}`;
    const encodedText = encodeURIComponent(text);
    const telegramUrl = `https://t.me/kanggurau?text=${encodedText}`;

    trackEvent('contact_intent', { method: 'telegram', page: window.location.pathname });
    window.open(telegramUrl, '_blank');

    setIsLoading(false);
    setIsSubmitted(true);

    toast({
      title: 'Redirecting to Telegram... ✈️',
      description: "Your message has been prepared. Please send it in the Telegram chat.",
    });

    // Reset after delay
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', message: '' });
    }, 3000);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="section-padding section-atmosphere bg-background border-t border-border"
      style={{ opacity: prefersReducedMotion() ? 1 : 0 }}
    >
      <div className="container-site max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <span className="chip mb-4">Contact</span>
          <h2 className="font-display uppercase font-light text-3xl sm:text-4xl tracking-[0.03em] text-foreground mb-4">
            Let's Connect
          </h2>
          <p className="text-xs uppercase tracking-wide leading-relaxed text-muted-foreground">
            Mau belajar bareng atau diskusi automasi yang kepake di kerjaan? Gas.
          </p>
        </div>

        {/* Contact Form */}
        <div className="contact-panel motion-panel bg-card p-6 sm:p-8 border border-border rounded-none">
          {isSubmitted ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-foreground mx-auto mb-4" />
              <h3 className="font-sans uppercase font-bold text-sm tracking-wider mb-2">
                Message Sent!
              </h3>
              <p className="text-muted-foreground text-xs uppercase tracking-wide leading-relaxed">
                Thanks for reaching out. I'll get back to you soon! 🙌
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[10px] uppercase font-bold tracking-wider text-foreground">Name</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Your name"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`rounded-none border-border ${errors.name ? 'border-destructive' : ''}`}
                />
                {errors.name && (
                  <p className="text-xs font-bold uppercase tracking-wider text-destructive">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[10px] uppercase font-bold tracking-wider text-foreground">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`rounded-none border-border ${errors.email ? 'border-destructive' : ''}`}
                />
                {errors.email && (
                  <p className="text-xs font-bold uppercase tracking-wider text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-[10px] uppercase font-bold tracking-wider text-foreground">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="What's on your mind?"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className={`rounded-none border-border ${errors.message ? 'border-destructive' : ''}`}
                />
                {errors.message && (
                  <p className="text-xs font-bold uppercase tracking-wider text-destructive">{errors.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full rounded-none bg-foreground text-background border border-foreground hover:bg-background hover:text-foreground font-bold uppercase"
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Social Links */}
        <div className="flex justify-center gap-4 mt-8">
          {socialLinks.map((link) => (
            <a
              key={link.platform}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('social_profile_clicked', { platform: link.platform })}
              aria-label={link.platform}
              className="w-12 h-12 rounded-none bg-card border border-border flex items-center justify-center text-foreground hover:bg-foreground hover:text-background transition-colors"
            >
              {iconMap[link.icon] ?? <span className="text-[10px] font-bold">{link.platform}</span>}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Contact;
